"use client";

import { LoginProfileError, loginFailureMessage } from "@/lib/auth/login-error";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useShallow } from "zustand/react/shallow";
import { fetchWithAuth, HttpError, refreshSession, logoutSession, invalidateHttpSessionRequests, readSharedSessionRevision } from "@/lib/http/client";
import { useAuthStore, type OrganizationContext, type AuthPhase } from "@/store/use-auth-store";
import { useAcademicYearStore } from "@/store/use-academic-year-store";
import { useCurrentAcademicYearState } from "@/store/use-current-academic-year-state";
import { deriveOrgState, type OrgState } from "@/lib/org-state";
import {
  clearClientSessionArtifacts,
  clearAuthReturnTarget,
  clearLogoutNavigationInProgress,
  markLogoutNavigationInProgress,
  authStepHref,
  storeReturnUrl,
} from "@/lib/auth-session";
import type { OrganizationRole, PermissionKey, User, AuthContext } from "@/types";
import { showToastOnce } from "@/utils/toast";
import {
  AUTH_DEBUG,
  stripAppBasePath,
  withAppBasePath,
} from "@/utils/env";
import { audit } from "@/lib/audit/audit.client";
import { toSafeAppRedirect } from "@/lib/safe-redirect";

type AuthEnvelope = {
  user: User;
  org?: OrganizationContext | null;
  organization?: OrganizationContext | null;
  roles: OrganizationRole[];
  /** Efektivní role aktuálního kontextu (multi-role, guardian Etapa A). */
  activeRole?: OrganizationRole | null;
  permissions: PermissionKey[];
  context: AuthContext;
};

type SwitchOrganizationResponse = AuthEnvelope & {
  organization?: OrganizationContext | null;
  membership?: { id: string; role: OrganizationRole; organizationId: string } | null;
};

type UseOrgResponse = AuthEnvelope & {
  organization?: OrganizationContext | null;
};

export type UseAuthResult = {
  user: User | null;
  org: OrganizationContext | null;
  roles: OrganizationRole[];
  activeRole: OrganizationRole | null;
  permissions: PermissionKey[];
  context: AuthContext | null;
  isHydrated: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  authPhase: AuthPhase;
  isLoggingOut: boolean;
  hasOrganization: boolean;
  orgState: OrgState;
  authStatus:
    | "anonymous"
    | "authenticating"
    | "authenticated"
    | "refreshing"
    | "unauthenticated";
  isOffline: boolean;
  needsSessionRetry?: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  logout: (options?: { returnTo?: string | null }) => Promise<void>;
  syncProfile: (options?: { force?: boolean }) => Promise<AuthEnvelope>;
  switchOrganization: (membershipId: string) => Promise<void>;
  switchToOrganizationByOrgId: (orgId: string) => Promise<AuthContext | null>;
  /** Přepnutí aktivního role-kontextu v rámci aktivního membershipu (multi-role). */
  switchRole: (role: OrganizationRole, options?: { returnTo?: string }) => Promise<void>;
};

type AuthResponse = AuthEnvelope;

export type LoginPayload = {
  email: string;
  password: string;
};

// Anonymous-safe pages must never probe /auth/me just to render public content.
// Join is intentionally excluded: an existing cookie must be recovered there
// so an authenticated invite flow can continue without bouncing through login.
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/public-library",
  "/eduto",
  "/handbook",
];
const isPublicRoutePath = (path: string) =>
  PUBLIC_ROUTES.includes(path) || path.startsWith("/reset-password/");


// Every useAuth consumer shares one verified profile request. Cached identity
// and the readable CSRF cookie are recovery hints, never authentication proof.
type ProfileSyncOptions = { force?: boolean; background?: boolean; discardPrevious?: boolean; recoveryHint?: boolean };
type ProfileRequest = { generation: number; controller: AbortController; promise: Promise<AuthEnvelope> };
let profileRequest: ProfileRequest | null = null;
let sessionGeneration = 0;
let applyingProfile = false;

class SupersededProfileError extends Error {
  constructor() { super("Session changed while verifying the profile"); this.name = "SupersededProfileError"; }
}

function sessionFingerprint(value: {
  user?: { id?: string; organizationId?: string | null } | null;
  org?: { id?: string } | null;
  activeRole?: string | null;
  context?: { mode?: string } | null;
  hadSession?: boolean;
}): string {
  return JSON.stringify([
    value.user?.id ?? null, value.org?.id ?? value.user?.organizationId ?? null,
    value.activeRole ?? null, value.context?.mode ?? null, Boolean(value.hadSession),
  ]);
}

function hasSessionHint(): boolean {
  const state = useAuthStore.getState();
  return Boolean(state.hadSession || state.user || (
    typeof document !== "undefined" &&
    document.cookie.split(";").some((cookie) => /^ss_csrf=.+/.test(cookie.trim()))
  ));
}

function invalidateProfileRequest(): Promise<AuthEnvelope> | null {
  const previous = profileRequest;
  sessionGeneration += 1;
  profileRequest = null;
  previous?.controller.abort();
  return previous?.promise ?? null;
}

const SESSION_CHANGE_KEY = "skillstorm_session_change";
type SessionChange = { kind: "changed" | "signed-out"; id: string };

function canCommitMutation(expected: number, allowLogout = false): boolean {
  return expected === sessionGeneration &&
    (allowLogout || useAuthStore.getState().authPhase !== "LOGGING_OUT");
}
function requireCurrentMutation(expected: number, allowLogout = false): void {
  if (!canCommitMutation(expected, allowLogout)) throw new SupersededProfileError();
}

// Also cover logout initiated by the HTTP client and identity changes outside
// this hook. A late /auth/me response must never restore the previous identity.
useAuthStore.subscribe((next, previous) => {
  if (applyingProfile) return;
  if (
    (next.authPhase === "LOGGING_OUT" && previous.authPhase !== "LOGGING_OUT") ||
    (previous.user && sessionFingerprint(next) !== sessionFingerprint(previous))
  ) invalidateProfileRequest();
});

function applyProfileState(update: () => void): void {
  applyingProfile = true;
  try { update(); } finally { applyingProfile = false; }
}

function definitiveSessionFailure(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
}

function synchronizeProfile(options: ProfileSyncOptions = {}): Promise<AuthEnvelope> {
  if (profileRequest && !options.force) return profileRequest.promise;
  if (options.force) invalidateProfileRequest();
  const initial = useAuthStore.getState();
  if (initial.authPhase === "LOGGING_OUT") return Promise.reject(new SupersededProfileError());
  const generation = sessionGeneration;
  const revision = readSharedSessionRevision();
  const controller = new AbortController();
  const keepVerified = options.background === true && !options.discardPrevious && initial.authStatus === "authenticated";
  const background = options.background === true && keepVerified;
  const hadHint = options.recoveryHint === true || hasSessionHint();
  const locallyCurrent = () => generation === sessionGeneration &&
    useAuthStore.getState().authPhase !== "LOGGING_OUT";
  const current = () => locallyCurrent() && revision === readSharedSessionRevision();
  const requireCurrent = () => { if (!current()) throw new SupersededProfileError(); };

  if (!background) {
    initial.setLoading(true);
    initial.setAuthStatus("authenticating");
  }
  const meConfig = {
    retries: 0, skipAuthRetry: true, cache: "no-store" as RequestCache,
    signal: controller.signal,
  };
  // Start in a microtask so the shared request is visible before any caller can
  // start another bootstrap in response to a synchronous store update.
  const promise = Promise.resolve().then(async () => {
    try {
      requireCurrent();
      let profile: AuthEnvelope;
      try {
        profile = await fetchWithAuth<AuthEnvelope>("GET", "/auth/me", meConfig);
      } catch (error) {
        requireCurrent();
        // A network/server failure is not an expired credential.
        if (!(error instanceof HttpError) || error.status !== 401) throw error;
        if (!background) useAuthStore.getState().setAuthStatus("refreshing");
        await refreshSession();
        requireCurrent();
        profile = await fetchWithAuth<AuthEnvelope>("GET", "/auth/me", meConfig);
      }
      requireCurrent();
      applyProfileState(() => {
        const state = useAuthStore.getState();
        state.setProfile(profile);
        state.setHadSession(true);
        state.setAuthStatus("authenticated");
      });
      return profile;
    } catch (error) {
      if (current()) applyProfileState(() => {
        const state = useAuthStore.getState();
        if (definitiveSessionFailure(error)) {
          state.clearAuthState();
        } else if (keepVerified) {
          state.setAuthStatus("authenticated");
        } else {
          // Do not display a persisted, unverified profile. Retain only a retry
          // hint so a brief outage does not demand a password or lose intent.
          state.setProfile({ user: null, org: null, roles: [], permissions: [], context: null });
          state.setHadSession(hadHint);
          state.setAuthStatus("anonymous");
        }
      });
      else if (locallyCurrent()) applyProfileState(() => {
        // A shared revision may change before its storage event is delivered
        // (or when storage is unavailable). Keep protected UI closed for retry.
        const state = useAuthStore.getState();
        state.setProfile({ user: null, org: null, roles: [], permissions: [], context: null });
        state.setHadSession(true);
        state.setAuthStatus("anonymous");
      });
      throw error;
    } finally {
      if (locallyCurrent()) {
        useAuthStore.getState().setLoading(false);
        useAuthStore.getState().setHydrated(true);
      }
      if (profileRequest?.generation === generation && profileRequest.controller === controller) {
        profileRequest = null;
      }
    }
  });
  profileRequest = { generation, controller, promise };
  return promise;
}

let authListenerUsers = 0;
let removeAuthListeners: (() => void) | null = null;

function subscribeToBrowserSession(): () => void {
  if (typeof window === "undefined") return () => undefined;
  authListenerUsers += 1;
  if (!removeAuthListeners) {
    let scheduled: ReturnType<typeof setTimeout> | null = null;
    const verify = () => {
      if (scheduled || !hasSessionHint() || useAuthStore.getState().loading || useAuthStore.getState().authPhase === "LOGGING_OUT") return;
      scheduled = setTimeout(() => {
        scheduled = null;
        if (useAuthStore.getState().loading || useAuthStore.getState().authPhase === "LOGGING_OUT") return;
        void synchronizeProfile({ background: true }).catch(() => undefined);
      }, 0);
    };
    const visible = () => { if (document.visibilityState === "visible") verify(); };
    const storage = (event: StorageEvent) => {
      if (event.key !== SESSION_CHANGE_KEY || !event.newValue) return;
      let incoming: SessionChange;
      try {
        incoming = JSON.parse(event.newValue) as SessionChange;
        if (!incoming || typeof incoming.id !== "string" ||
            !["changed", "signed-out"].includes(incoming.kind)) return;
      } catch { return; }
      const revision = readSharedSessionRevision();
      if (revision && revision !== incoming.id) return;
      invalidateHttpSessionRequests();
      invalidateProfileRequest();
      const state = useAuthStore.getState();
      if (incoming.kind === "signed-out") {
        state.clearAuthState();
        return;
      }
      if (state.authPhase === "LOGGING_OUT") return;
      // Signals carry no cached identity. Validate the actual shared cookies,
      // and never echo a received signal back to sibling tabs.
      void synchronizeProfile({ discardPrevious: true, recoveryHint: true }).catch(() => undefined);
    };
    window.addEventListener("storage", storage);
    window.addEventListener("focus", verify);
    window.addEventListener("online", verify);
    document.addEventListener("visibilitychange", visible);
    removeAuthListeners = () => {
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", verify);
      window.removeEventListener("online", verify);
      document.removeEventListener("visibilitychange", visible);
      if (scheduled) clearTimeout(scheduled);
    };
  }
  return () => {
    authListenerUsers -= 1;
    if (authListenerUsers === 0) { removeAuthListeners?.(); removeAuthListeners = null; }
  };
}

/**
 * Hlavní klientský auth hook.
 *
 * Zodpovídá za:
 * - bootstrap session po načtení aplikace,
 * - synchronizaci profilu přes `/auth/me`,
 * - obnovu session přes refresh token,
 * - logout a přepínání aktivní organizace,
 * - odvození auth stavu pro guardy a layouty.
 *
 * Hook schválně drží auth logiku na jednom místě, aby zbytek UI
 * pracoval už jen s jednotným auth stavem a nemusel řešit refresh flow.
 */
export const useAuth = (): UseAuthResult => {
  const pathname = usePathname();
  const router = useRouter();
  const sessionRecoveryAttemptedRef = useRef(false);
  const {
    authPhase,
    user,
    org,
    roles,
    activeRole,
    permissions,
    context,
    loading,
    authStatus,
    offline,
    hydrated,
    hadSession,
    setProfile,
    setLoading,
    setOffline,
    setHadSession,
    setHydrated,
    beginLogout,
    clearAuthState,
  } = useAuthStore(
    useShallow((state) => ({
      authPhase: state.authPhase,
      user: state.user,
      org: state.org,
      roles: state.roles,
      activeRole: state.activeRole,
      permissions: state.permissions,
      context: state.context,
      loading: state.loading,
      authStatus: state.authStatus,
      offline: state.offline,
      hydrated: state.hydrated,
      hadSession: state.hadSession,
      setProfile: state.setProfile,
      setLoading: state.setLoading,
      setOffline: state.setOffline,
      setHadSession: state.setHadSession,
      setHydrated: state.setHydrated,
      beginLogout: state.beginLogout,
      clearAuthState: state.clearAuthState,
    })),
  );

  const isLoggingOut = authPhase === "LOGGING_OUT";
  const appRelativePathname = pathname ? stripAppBasePath(pathname) : pathname;

  const isPublicRoute = useMemo(
    () => (appRelativePathname ? isPublicRoutePath(appRelativePathname) : false),
    [appRelativePathname],
  );

  const syncProfile = useCallback(
    (options?: { force?: boolean }) => synchronizeProfile(options),
    [],
  );

  useEffect(() => subscribeToBrowserSession(), []);

  useEffect(() => {
    // Keep route guards from replacing an explicit logout continuation while
    // the protected page is still mounted. Arrival on a public route ends it.
    if (isPublicRoute) clearLogoutNavigationInProgress();
  }, [appRelativePathname, isPublicRoute]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateStatus = () => setOffline(!navigator.onLine);
    updateStatus();
    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
    return () => {
      window.removeEventListener("online", updateStatus);
      window.removeEventListener("offline", updateStatus);
    };
  }, [setOffline]);

  useEffect(() => {
    if (!AUTH_DEBUG) return;
    console.log(
      "%c[AUTH][STATE]",
      "color:#9333ea;font-weight:600",
      { pathname, hydrated, authStatus, loading, userId: user?.id ?? null },
    );
  }, [pathname, hydrated, authStatus, loading, user?.id]);

  useEffect(() => {
    if (typeof window === "undefined" || isLoggingOut || loading) return;
    if (authStatus === "authenticated") {
      sessionRecoveryAttemptedRef.current = false;
      setHydrated(true);
      return;
    }
    if (authStatus === "authenticating" || authStatus === "refreshing") return;
    if (authStatus === "unauthenticated" && hydrated) return;
    if (isPublicRoute && !hasSessionHint()) {
      setHydrated(true);
      return;
    }
    if (sessionRecoveryAttemptedRef.current) return;
    sessionRecoveryAttemptedRef.current = true;
    setHydrated(false);
    // Cookie validation, including refresh-only sessions, is shared across all
    // consumers. Cached profile data alone never marks the session ready.
    void syncProfile().catch(() => undefined);
  }, [loading, authStatus, syncProfile, isPublicRoute, hadSession, hydrated, setHydrated, isLoggingOut]);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const beforeLogin = useAuthStore.getState();
      invalidateProfileRequest();
      const mutationGeneration = sessionGeneration;
      setLoading(true);
      let sessionIssued = false;
      try {
        await fetchWithAuth<AuthResponse | undefined>("POST", "/auth/login", {
          body: payload,
          skipAuthRetry: true,
        });
        requireCurrentMutation(mutationGeneration);
        // Session issuance stays lean; hydrate the complete RBAC/readiness
        // profile through the canonical endpoint before marking auth ready.
        sessionIssued = true;
        applyProfileState(() => setHadSession(true));
        await synchronizeProfile();
        requireCurrentMutation(mutationGeneration);
        audit({ action: "LOGIN" });
      } catch (error) {
        if (!canCommitMutation(mutationGeneration)) throw error;
        if (!sessionIssued) {
          const state = useAuthStore.getState();
          state.setAuthStatus(beforeLogin.authStatus === "authenticated" ? "authenticated" : "anonymous");
          state.setHydrated(true);
        }
        const failure = sessionIssued ? new LoginProfileError(error) : error;
        const status = error instanceof HttpError ? error.status : undefined;
        showToastOnce(failure instanceof LoginProfileError ? failure.message : loginFailureMessage(status), { type: "error" });
        throw failure;
      } finally {
        if (canCommitMutation(mutationGeneration)) setLoading(false);
      }
    },
    [setLoading, setHadSession],
  );

  const logout = useCallback(async (options?: { returnTo?: string | null }) => {
    const returnTo = options?.returnTo ? toSafeAppRedirect(options.returnTo) : null;
    const loginTarget = authStepHref("/login", returnTo);
    const beforeLogout = useAuthStore.getState();
    invalidateProfileRequest();
    beginLogout();
    const mutationGeneration = sessionGeneration;
    markLogoutNavigationInProgress();
    try {
      // Keep cookies and identity until the server acknowledges logout. A lost
      // response must not make account switching appear successful.
      await logoutSession();
      requireCurrentMutation(mutationGeneration, true);
    } catch (error) {
      if (!canCommitMutation(mutationGeneration, true)) throw error;
      useAuthStore.getState().setAuthStatus(beforeLogout.authStatus === "authenticated" ? "authenticated" : "unauthenticated");
      useAuthStore.getState().setLoading(false);
      useAuthStore.getState().setHydrated(true);
      clearLogoutNavigationInProgress();
      showToastOnce("Odhlášení se nepodařilo potvrdit. Zkontroluj připojení a zkus to znovu.", { type: "error" });
      throw error;
    }
    clearClientSessionArtifacts();
    clearAuthReturnTarget();
    if (returnTo) storeReturnUrl(returnTo);
    useAcademicYearStore.getState().clearAll();
    router.replace(loginTarget);
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        if (stripAppBasePath(window.location.pathname).startsWith("/app")) {
          window.location.replace(withAppBasePath(loginTarget));
        }
      }, 0);
    }

    clearAuthState();
  }, [beginLogout, clearAuthState, router]);

  const switchOrganization = useCallback(
    async (membershipId: string) => {
      const previousOrgId = org?.id ?? null;
      const beforeSwitch = useAuthStore.getState();
      invalidateProfileRequest();
      const mutationGeneration = sessionGeneration;
      setLoading(true);
      try {
        const res = await fetchWithAuth<SwitchOrganizationResponse>(
          "POST",
          "/auth/switch-organization",
          { body: { membershipId }, skipAuthRetry: true },
        );
        requireCurrentMutation(mutationGeneration);
        const nextOrg = res?.organization ?? res?.org ?? null;
        const nextUser = res?.user ?? null;
        const nextRoles = res?.roles ?? [];
        const nextPermissions = res?.permissions ?? [];
        applyProfileState(() => setProfile({
          user: nextUser ?? null,
          org: nextOrg,
          roles: nextRoles,
          activeRole: res?.activeRole ?? null,
          permissions: nextPermissions,
          context: res?.context ?? null,
        }));
        if (previousOrgId) {
          useAcademicYearStore.getState().clearOrg(previousOrgId);
          useCurrentAcademicYearState.getState().resetOrg(previousOrgId);
        }
        const nextOrgId = res?.organization?.id ?? res?.org?.id ?? null;
        if (nextOrgId) {
          useCurrentAcademicYearState.getState().resetOrg(nextOrgId);
        }
        if (typeof window !== "undefined") {
          window.localStorage.setItem("skillstorm_activeMembershipId", membershipId);
          window.localStorage.setItem("skillstorm_activeMembershipSwitchedAt", String(Date.now()));
        }
        useAuthStore.getState().setAuthStatus("authenticated");
        useAuthStore.getState().setHydrated(true);
        router.replace("/app");
        showToastOnce("Přepnuli jsme aktivní organizaci.", {
          type: "info",
        });
      } catch (error) {
        if (!canCommitMutation(mutationGeneration)) throw error;
        useAuthStore.getState().setAuthStatus(beforeSwitch.authStatus === "authenticated" ? "authenticated" : "anonymous");
        useAuthStore.getState().setHydrated(true);
        showToastOnce("Nepodařilo se přepnout organizaci.", { type: "error" });
        throw error;
      } finally {
        if (canCommitMutation(mutationGeneration)) setLoading(false);
      }
    },
    [org?.id, setLoading, setProfile, router],
  );

  const switchRole = useCallback(
    async (role: OrganizationRole, options?: { returnTo?: string }) => {
      const beforeSwitch = useAuthStore.getState();
      invalidateProfileRequest();
      const mutationGeneration = sessionGeneration;
      setLoading(true);
      try {
        const res = await fetchWithAuth<SwitchOrganizationResponse>(
          "POST",
          "/auth/switch-role",
          { body: { role }, skipAuthRetry: true },
        );
        requireCurrentMutation(mutationGeneration);
        const nextOrg = res?.organization ?? res?.org ?? null;
        applyProfileState(() => setProfile({
          user: res?.user ?? null,
          org: nextOrg,
          roles: res?.roles ?? [],
          activeRole: res?.activeRole ?? role,
          permissions: res?.permissions ?? [],
          context: res?.context ?? null,
        }));
        useAuthStore.getState().setAuthStatus("authenticated");
        useAuthStore.getState().setHydrated(true);
        router.replace(toSafeAppRedirect(options?.returnTo ?? "") ?? "/app");
        showToastOnce(
          role === "PARENT"
            ? "Přepnuto do rodičovského zobrazení."
            : "Přepnuli jsme aktivní roli.",
          { type: "info" },
        );
      } catch (error) {
        if (!canCommitMutation(mutationGeneration)) throw error;
        useAuthStore.getState().setAuthStatus(beforeSwitch.authStatus === "authenticated" ? "authenticated" : "anonymous");
        useAuthStore.getState().setHydrated(true);
        showToastOnce("Nepodařilo se přepnout roli.", { type: "error" });
        throw error;
      } finally {
        if (canCommitMutation(mutationGeneration)) setLoading(false);
      }
    },
    [setLoading, setProfile, router],
  );

  const switchToOrganizationByOrgId = useCallback(
    async (orgId: string): Promise<AuthContext | null> => {
      const previousOrgId = org?.id ?? null;
      const beforeSwitch = useAuthStore.getState();
      invalidateProfileRequest();
      const mutationGeneration = sessionGeneration;
      setLoading(true);
      try {
        const res = await fetchWithAuth<UseOrgResponse>("POST", "/auth/use-org", {
          body: { orgId },
          skipAuthRetry: true,
        });
        if (AUTH_DEBUG || process.env.NODE_ENV === "development") {
          console.log("[useAuth] use-org response", {
            hasContext: !!res?.context,
            mode: res?.context?.mode,
            hasOrg: !!(res?.organization ?? res?.org),
            hasUser: !!res?.user,
            membershipsCount: res?.user?.memberships?.length ?? 0,
          });
        }
        if (!res?.context || res.context.mode !== "organization") {
          throw new Error("USE_ORG_FAILED_OR_BAD_CONTEXT");
        }
        const effectiveContext = res.context;
        requireCurrentMutation(mutationGeneration);
        const nextOrg = res?.organization ?? res?.org ?? null;
        if (res.user != null) {
          applyProfileState(() => setProfile({
            user: res.user,
            org: nextOrg,
            roles: res?.roles ?? [],
            permissions: res?.permissions ?? [],
            context: effectiveContext,
          }));
        } else {
          const profile = await fetchWithAuth<AuthEnvelope>("GET", "/auth/me", {
            retries: 0,
            skipAuthRetry: true,
          });
          requireCurrentMutation(mutationGeneration);
          applyProfileState(() => setProfile(profile));
        }
        if (previousOrgId) {
          useAcademicYearStore.getState().clearOrg(previousOrgId);
          useCurrentAcademicYearState.getState().resetOrg(previousOrgId);
        }
        if (orgId) {
          useCurrentAcademicYearState.getState().resetOrg(orgId);
        }
        useAuthStore.getState().setAuthStatus("authenticated");
        useAuthStore.getState().setHydrated(true);
        return effectiveContext;
      } catch (error) {
        if (!canCommitMutation(mutationGeneration)) throw error;
        useAuthStore.getState().setAuthStatus(beforeSwitch.authStatus === "authenticated" ? "authenticated" : "anonymous");
        useAuthStore.getState().setHydrated(true);
        throw error;
      } finally {
        if (canCommitMutation(mutationGeneration)) setLoading(false);
      }
    },
    [org?.id, setLoading, setProfile],
  );

  const hasOrganization = context?.mode === "organization";

  const orgState = useMemo(
    () => {
      if (context?.mode === "platform") {
        return "HAS_ORG" as OrgState;
      }
      return deriveOrgState({
        memberships: user?.memberships,
        organization: org,
      });
    },
    [context?.mode, user?.memberships, org],
  );

  const value = useMemo(
    () => ({
      user,
      org,
      roles,
      activeRole,
      permissions,
      context,
      authPhase,
      isLoggingOut,
      isHydrated: hydrated,
      isLoading:
        loading ||
        !hydrated ||
        authStatus === "authenticating" ||
        authStatus === "refreshing",
      isAuthenticated: authStatus === "authenticated",
      hasOrganization,
      orgState,
      authStatus,
      isOffline: offline,
      needsSessionRetry: hydrated && authStatus === "anonymous" && hadSession,
      login,
      logout,
      syncProfile,
      switchOrganization,
      switchToOrganizationByOrgId,
      switchRole,
    }),
    [
      user,
      org,
      roles,
      activeRole,
      permissions,
      context,
      authPhase,
      isLoggingOut,
      hydrated,
      loading,
      authStatus,
      offline,
      hadSession,
      hasOrganization,
      orgState,
      login,
      logout,
      syncProfile,
      switchOrganization,
      switchToOrganizationByOrgId,
      switchRole,
    ],
  );

  return value;
};
